import { DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import {
  BaseCampaignPostItemDTO,
  BaseMgSupplierCampaignItem,
  BaseMgSupplierCampaignItemLookupDTO,
  MgSupplierItemLookupDTO,
} from '@retail/campaign/types';
import {
  SuspenseWithSentryErrorBoundary,
  withDialogSuspenseErrorBoundary,
} from '@retail/components';
import { AddItemsTabs } from '@retail/my-assortment/components';
import { AddItemsTab } from '@retail/my-assortment/types';
import {
  ContainedButton,
  Dialog,
  OutlinedButton,
  Step,
  Stepper2,
  TextButton,
} from '@shared/components';
import React, { useCallback, useEffect, useState } from 'react';
import { useAddCampaignItemsActions } from '@retail/campaign/context';
import {
  AddCampaignItemsSchema,
  AddItemsStep,
  BaseCampaignImportItem,
  BaseCampaignItemSchema,
  CampaignItemsImportConfig,
  GetCampaignItemsColumnsFn,
  ImportItemApiErrors,
  UploadCampaignItems,
  useAddItemsStore,
} from '@retail/campaign/components';
import { FormProvider, SubmitErrorHandler, SubmitHandler, UseFormReturn } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { useToast } from '@retail/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { AddItemsOverviewContainer } from './AddItemsOverviewContainer';
import { useImportItemsStore } from '@retail/products/components';

interface CampaignDetailsAddItemsDialogProps<
  TMgSupplierCampaignItem extends BaseMgSupplierCampaignItem,
  TPostItemDTO extends BaseCampaignPostItemDTO,
  TLookupDTO extends BaseMgSupplierCampaignItemLookupDTO,
  TItemSchema extends BaseCampaignItemSchema,
  TCampaignImportItem extends BaseCampaignImportItem
> {
  open: boolean;
  campaignId: string;
  onClose: () => void;
  campaignType: 'brand' | 'member';
  itemsFormMethods: UseFormReturn<AddCampaignItemsSchema<TItemSchema>>;
  importConfig: CampaignItemsImportConfig<TCampaignImportItem>;
  getInitSchemaItem: (item: TMgSupplierCampaignItem) => TItemSchema;
  lookupItemErrors: TLookupDTO[];
  setLookupItemErrors: (errors: TLookupDTO[]) => void;

  mapLookupItemToSchemaItem: (
    lookupItem: TMgSupplierCampaignItem,
    importItem: TCampaignImportItem | undefined
  ) => TItemSchema;

  renderItemSearch: (props: {
    selectedItems: any[];
    setSelectedItems: (items: any[]) => void;
  }) => React.ReactNode;

  transformToPostDTO: (formItem: TItemSchema, lookupItem: TMgSupplierCampaignItem) => TPostItemDTO;

  getCampaignItemsColumns: GetCampaignItemsColumnsFn<TMgSupplierCampaignItem>;

  isPendingLookupItems: boolean;
  performItemsLookup: (dto: MgSupplierItemLookupDTO[]) => Promise<TMgSupplierCampaignItem[]>;
}

function AddItemsToCampaignDialogBaseComponent<
  TMgSupplierCampaignItem extends BaseMgSupplierCampaignItem,
  TPostItemDTO extends BaseCampaignPostItemDTO,
  TLookupDTO extends BaseMgSupplierCampaignItemLookupDTO,
  TItemSchema extends BaseCampaignItemSchema,
  TCampaignImportItem extends BaseCampaignImportItem
>({
  open,
  onClose,
  campaignId,
  itemsFormMethods,
  campaignType,
  importConfig,
  getInitSchemaItem,
  mapLookupItemToSchemaItem,
  renderItemSearch,
  transformToPostDTO,
  getCampaignItemsColumns,
  isPendingLookupItems,
  performItemsLookup,
  lookupItemErrors,
  setLookupItemErrors,
}: CampaignDetailsAddItemsDialogProps<
  TMgSupplierCampaignItem,
  TPostItemDTO,
  TLookupDTO,
  TItemSchema,
  TCampaignImportItem
>) {
  const t = useAppTFunction();
  const [, setURLSearchParams] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();

  const steps: Step<AddItemsStep>[] = [
    {
      step: 'FIND_ITEMS',
      label: t('campaign.actions.addItems'),
    },
    {
      step: 'EDIT_PRICES',
      label: t('campaign.actions.editCampaignPrices'),
    },
  ];

  const { mutateAddCampaignItems, isLoadingAddToCampaign: isSubmitting } =
    useAddCampaignItemsActions<TPostItemDTO>({
      campaignId,
      type: campaignType,
    });

  const [selectedTab, setSelectedTab] = useState<AddItemsTab>('ITEM_SEARCH');

  const {
    selectedItems = [],
    activeStep,
    setActiveStep,
    setSelectedItems,
    resetState,
  } = useAddItemsStore();

  const { run, clearState: clearCampaignItemsState } = useImportItemsStore<TCampaignImportItem>();

  const [lookupItems, setLookupItems] = useState<TMgSupplierCampaignItem[]>([]);

  const handleSetSelectedTab = (tab: AddItemsTab) => {
    if (selectedTab === 'ITEM_SEARCH') itemsFormMethods.setValue('items', {});
    else if (selectedTab === 'IMPORT') clearCampaignItemsState();

    setSelectedTab(tab);
  };

  const onImportSuccess = useCallback(
    (importedItems: TCampaignImportItem[]) => {
      setLookupItemErrors([]);
      performItemsLookup(
        importedItems.map((item) => ({
          mgItemNumber: item.mgItemNumber,
          gtinCode: item.gtinCode,
          supplierId: item.mgSupplierId,
        }))
      )
        .then((lookupItems) => {
          if (!lookupItems.length) return;
          const formItems: AddCampaignItemsSchema<TItemSchema>['items'] = lookupItems.reduce(
            (prev, lookupItem) => {
              const importItem = importedItems.find(
                (importItem) => importItem.mgItemNumber === lookupItem.mgItemNumber
              );
              return {
                ...prev,
                [lookupItem.mgSupplierItemId + '']: mapLookupItemToSchemaItem(
                  lookupItem,
                  importItem
                ),
              };
            },
            {}
          );
          setLookupItems(lookupItems);
          itemsFormMethods.setValue('items', formItems);
          setActiveStep('EDIT_PRICES');
        })
        .catch((error) => {
          toast.error(t('campaign.error.genericError'));
          console.error(error);
        });
    },
    [
      itemsFormMethods,
      mapLookupItemToSchemaItem,
      performItemsLookup,
      setActiveStep,
      setLookupItemErrors,
      t,
      toast,
    ]
  );

  const onImportError = useCallback(() => {
    setLookupItemErrors([]);
  }, []);

  const goToEditPrices = useCallback(() => {
    let requestDto: MgSupplierItemLookupDTO[];
    if (selectedTab === 'ITEM_SEARCH') {
      requestDto = selectedItems!.map((item) => ({
        mgItemNumber: item.mgItemNumber,
        supplierId: item.mgSupplierId,
      }));
      performItemsLookup(requestDto)
        .then((items) => {
          const formItems: AddCampaignItemsSchema<TItemSchema>['items'] = items.reduce(
            (prev, item) => ({
              ...prev,
              [item.mgSupplierItemId + '']: getInitSchemaItem(item),
            }),
            {}
          );
          setLookupItems(items);
          itemsFormMethods.setValue('items', formItems);

          setURLSearchParams();
          setActiveStep('EDIT_PRICES');
        })
        .catch((error) => {
          toast.error(t('campaign.error.genericError'));
          console.error(error);
        });
    } else {
      setURLSearchParams();
      setActiveStep('EDIT_PRICES');
    }
  }, [
    getInitSchemaItem,
    itemsFormMethods,
    performItemsLookup,
    selectedItems,
    selectedTab,
    setActiveStep,
    setURLSearchParams,
    t,
    toast,
  ]);

  const onSubmit: SubmitHandler<AddCampaignItemsSchema<TItemSchema>> = useCallback(
    ({ items }) => {
      const itemEntries = Object.entries(items);
      const dto: TPostItemDTO[] = itemEntries.map(([mgSupplierItemId, formItem]) => {
        const searchItem = lookupItems!.find((i) => i.mgSupplierItemId + '' === mgSupplierItemId)!;
        return transformToPostDTO(formItem, searchItem);
      });
      mutateAddCampaignItems({ body: dto })
        .then(() => {
          toast.success(
            itemEntries.length === 1
              ? t('campaign.messages.itemAdded', { count: 1 })
              : t('campaign.messages.itemsAdded', { count: itemEntries.length })
          );

          queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'items'] });
          onClose();
        })
        .catch((error) => {
          console.error(error);
          toast.error(t('campaign.error.genericError'));
        });
    },
    [
      campaignId,
      lookupItems,
      mutateAddCampaignItems,
      onClose,
      queryClient,
      t,
      toast,
      transformToPostDTO,
    ]
  );

  const onSubmitErrorHandler: SubmitErrorHandler<AddCampaignItemsSchema<TItemSchema>> = useCallback(
    (errors) => {
      const errorItemNumbers = errors.items ? Object.keys(errors.items) : [];

      const itemsSortedErrorsFirst = [...(selectedItems ?? [])].sort((a, b) => {
        const aIndex = errorItemNumbers.indexOf(a.mgSupplierItemId + '');
        const bIndex = errorItemNumbers.indexOf(b.mgSupplierItemId + '');
        return bIndex - aIndex;
      });
      setSelectedItems(itemsSortedErrorsFirst);
      setURLSearchParams();
    },
    [selectedItems, setSelectedItems, setURLSearchParams]
  );

  const submitItemPrices = itemsFormMethods.handleSubmit(onSubmit, onSubmitErrorHandler);

  const goBackToFindItems = useCallback(() => {
    setActiveStep('FIND_ITEMS');
    setURLSearchParams();
    if (selectedTab === 'ITEM_SEARCH') {
      itemsFormMethods.reset();
      setSelectedItems([]);
      setLookupItemErrors([]);
      setLookupItems([]);
    }
  }, [
    itemsFormMethods,
    selectedTab,
    setActiveStep,
    setLookupItemErrors,
    setSelectedItems,
    setURLSearchParams,
  ]);

  useEffect(() => {
    resetState();
    clearCampaignItemsState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xxl"
      slotProps={{
        paper: { className: 'padding-0 gap-0' },
      }}
    >
      <DialogTitle
        sx={{
          paddingX: ({ spacing }) => spacing(4),
          paddingTop: ({ spacing }) => spacing(4),
          paddingBottom: ({ spacing }) => spacing(2),
        }}
      >
        {t('campaign.actions.addItems')}
        <Stepper2 steps={steps} activeStep={activeStep} labelOrientation="horizontal" />
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexGrow: 1,
          height: '100vh',
          padding: 0,
          borderBottom: (theme) => `1px solid ${theme.vars.palette.divider}`,
        }}
      >
        {activeStep === 'EDIT_PRICES' && (
          <FormProvider {...itemsFormMethods}>
            <AddItemsOverviewContainer
              items={lookupItems ?? []}
              getCampaignItemsColumns={getCampaignItemsColumns}
            />
          </FormProvider>
        )}

        {activeStep === 'FIND_ITEMS' && (
          <Stack flexGrow={1} pt={2}>
            <Stack flexGrow={0} mb={2}>
              <AddItemsTabs
                selectedTab={selectedTab}
                setSelectedTab={handleSetSelectedTab}
                includeCentralAssortments={false}
                sx={{ paddingLeft: ({ spacing }) => spacing(4) }}
              />
            </Stack>
            {selectedTab === 'ITEM_SEARCH' && (
              <SuspenseWithSentryErrorBoundary>
                {renderItemSearch({ selectedItems, setSelectedItems })}
              </SuspenseWithSentryErrorBoundary>
            )}

            {selectedTab === 'IMPORT' && (
              <Stack
                display="flex"
                flexGrow={1}
                justifyContent="center"
                alignItems="center"
                spacing={4}
              >
                <UploadCampaignItems
                  onRemoveFile={() => {
                    setLookupItems([]);
                    setLookupItemErrors([]);
                  }}
                  onImportSuccess={onImportSuccess}
                  onImportError={onImportError}
                  importConfig={importConfig}
                />
                {!!lookupItemErrors.length && (
                  <ImportItemApiErrors
                    items={(lookupItemErrors as any[]).map((item) => ({
                      reason: item.errorCode!,
                      ...item.input,
                    }))}
                  />
                )}
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: ({ spacing }) => `${spacing(2)} ${spacing(4)}` }}>
        {activeStep === 'FIND_ITEMS' && (
          <>
            <TextButton onClick={onClose} size="small">
              {t('calculus.cancel')}
            </TextButton>
            <ContainedButton
              disabled={!selectedItems?.length && !run?.summary.okCount}
              loading={isPendingLookupItems}
              onClick={goToEditPrices}
              size="small"
            >
              {t('campaign.actions.continueWithItems')}
            </ContainedButton>
          </>
        )}
        {activeStep === 'EDIT_PRICES' && (
          <>
            <TextButton onClick={onClose} size="small" loading={isSubmitting}>
              {t('calculus.cancel')}
            </TextButton>
            <OutlinedButton size="small" onClick={goBackToFindItems} disabled={isSubmitting}>
              {t('campaign.actions.goBackToFindItems')}
            </OutlinedButton>
            <ContainedButton onClick={submitItemPrices} loading={isSubmitting} size="small">
              {t('campaign.actions.addItemsToCampaign')}
            </ContainedButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export const AddItemsToCampaignDialogBase = withDialogSuspenseErrorBoundary(
  AddItemsToCampaignDialogBaseComponent
) as typeof AddItemsToCampaignDialogBaseComponent;
